import { trigger, state, style, transition, animate } from '@angular/animations';
import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { AppService, GRSSResponse, GRSSUserRoles, ValueIdPair } from 'src/app/_services/app.service';
import { ManageAccountsComponent } from '../manage-accounts.component';
import { Router } from '@angular/router';

@Component({
    selector: 'app-add-user-account',
    templateUrl: './add-user-account.component.html',
    animations: [
        trigger('slider', [
            state('*', style({
                height: 0
            })),
            state('show', style({
                height: '*'
            })),
            transition('* <=> show', [animate('400ms cubic-bezier(0.25, 0.8, 0.25, 1)')])
        ])
    ],
    standalone: false
})
export class AddUserAccountComponent implements OnInit {

  userAccount: UserAccount;
  roles: ValueIdPair[];
  markets: ValueIdPair[];
  isPasswordVisible;
  adminRoleName = GRSSUserRoles.Admin;
  operatorRoleName = GRSSUserRoles.Operator;
  isAccountEnabledOriginally: boolean = false;
  forceHideLoginAs: boolean = false;

  constructor(private translate: TranslateService, public router: Router, public service: AppService, public dialogRef: MatDialogRef<ManageAccountsComponent>, private http: HttpClient, @Inject(MAT_DIALOG_DATA) public data, @Inject('BASE_URL') private baseUrl: string) { }

  ngOnInit() {
    this.GetMarkets();
    this.GetRoles();
    this.InitializeForm();
    if (this.service.isUpdateUserAccount === true) {
      this.PopulateForm();
    }
    this.isAccountEnabledOriginally = this.userAccount.enabled;

    // Check if this is an impersonation session
    if (this.service.getCookie('Imp_Token') || this.service.currentUser.userId == this.userAccount.userId) {
      this.forceHideLoginAs = true;
    }
  }

  /** Gets all the roles from the server */
  GetRoles() {
    this.http.get<ValueIdPair[]>(this.baseUrl + 'api/account/getRoles').subscribe(result => {
      this.roles = result;
      if (this.userAccount.roleId === null) {
        this.userAccount.roleId = this.roles[this.roles.length - 1].id; // Roles were already ordered from the server. Here it'll assign lowest role as the id
      }

      if (this.userAccount.roleName === null) {
        this.userAccount.roleName = this.roles[this.roles.length - 1].value;
      }
    });
  }

  /** Gets all the markets from server */
  GetMarkets() {
    this.http.get<ValueIdPair[]>(this.baseUrl + 'api/account/getMarkets').subscribe(result => {
      this.markets = result;
    });
  }

  /** Initializes the form values */
  InitializeForm() {
    this.userAccount = {
      userId: '',
      generalMarketIds: null,
      username: '',
      password: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: null,
      mobile: null,
      roleId: null,
      roleName: null,
      roleIds: null,
      enabled: false,
      twoFactor: true,
      challenge: false,
      challengeAnswer: '',
      challengeQuestion: ''
    };
  }

  /** Submit button event for the form */
  OnSaveAccount() {
    if (this.userAccount.username && this.userAccount.email && this.userAccount.firstName && this.userAccount.lastName && this.userAccount.phone) {

      this.http.post<GRSSResponse>(this.baseUrl + 'api/account/verifyForm', this.userAccount).subscribe(result => {
        if (result.statusCode === 1) {
          if (this.userAccount.challenge) {
            if (this.userAccount.challengeQuestion && this.userAccount.challengeAnswer) {
              this.OnClose();
            }
          } else {
            this.OnClose();
          }
        } else {

          if (!this.userAccount.mobile) {
            this.userAccount.mobile = null;
          }

          this.service.SaveStatus(false, this.translate.instant('Accounts.Messages.msgHeaderUserAcc'), result.statusMessage, true);
        }
      });
    }
  }

  /** Closing the add/edit user account window with user account details */
  OnClose() {

    // Check if the user's current role is within the selected roles
    if (this.userAccount.roleIds.includes(this.userAccount.roleId) == false) {
      // Setting the top role as the main role of the user
      let hasRole = false;
      this.roles.forEach(role => {
        if (this.userAccount.roleIds.includes(role.id) && hasRole == false) {
          this.userAccount.roleId = role.id;
          hasRole = true;
        }
      });
    }

    // Adding all selected roles into the name
    this.userAccount.roleName = '';
    this.roles.forEach(role => {
      if (this.userAccount.roleIds.includes(role.id)) {
        if (this.userAccount.roleName != '') {
          this.userAccount.roleName += ', ';
        }
        this.userAccount.roleName += role.value;
      }
    });

    this.userAccount.challenge = false;
    this.dialogRef.close(this.userAccount);
    this.service.isUpdateUserAccount = false;
  }

  /** login as selected user */
  OnLoginAs() {
    if ((this.service.currentUser.userRole == GRSSUserRoles.Admin || (this.service.currentUser.userRole == GRSSUserRoles.Operator && this.userAccount.roleName != GRSSUserRoles.Operator)) && this.isAccountEnabledOriginally && this.userAccount.enabled) {
      this.http.get<GRSSResponse>(this.baseUrl + 'api/account/loginas/' + this.userAccount.userId).subscribe(result => {
        if (result.statusCode == 1) {
          this.service.currentUser = undefined;
          const temp = new Promise((resolve, reject) => {
            this.service.CheckAuth().then(res => {
              if (res) {
                this.dialogRef.close(null);
                this.router.navigate([this.service.currentUser.defaultUrl]);
                resolve(true);
              } else {
                resolve(false);
              }
            });
          });
        } else {
          this.service.SaveStatus(false, this.translate.instant('Accounts.Messages.msgHeaderUserAcc'), result.statusMessage);
        }
      }, error => {
        console.error(error);
      });
    }
  }

  /** Populates the form values with passing data from manage accounts (Parent) */
  PopulateForm() {
    this.userAccount = {
      userId: this.data.userId,
      generalMarketIds: this.data.generalMarketIds,
      username: this.data.username,
      password: '',
      firstName: this.data.firstName,
      lastName: this.data.lastName,
      email: this.data.email,
      phone: this.data.phone,
      mobile: this.data.mobile,
      roleId: this.data.roleId,
      roleName: this.data.roleName,
      roleIds: this.data.roleIds,
      enabled: this.data.enabled,
      twoFactor: this.data.twoFactor,
      challenge: this.data.challenge,
      challengeAnswer: this.data.challengeAnswer,
      challengeQuestion: this.data.challengeQuestion
    };
    // if (this.userAccount.mobile === '0') {
    //   this.userAccount.mobile = null;
    // }
  }

  /** Cancel button event */
  OnCancel() {
    this.dialogRef.close(null);
  }

  /**
   * Handles users selections on manager and readonly toggles. Users can only select one option.
   * @param ability Used to identify the toggled toggler (0 = Manager toggled, 1 = Readonly toggled)
   */
  OnAbilityChange(ability: number) {
    // if (this.userAccount.manager === true && this.userAccount.readonly === true) {
    //   if (ability === 0) {
    //     this.userAccount.readonly = false;
    //   } else if (ability === 1) {
    //     this.userAccount.manager = false;
    //   }
    // }
  }

  /**
   *  Validates a number
   * @param changed chaged from either mobile or phone entries
   */
  ValidateNumber(changed: number) {
    const allowedChars = '+ 0123456789-x';
    if (changed === 0) {
      // this.userAccount.phone = this.userAccount.phone.replace(/[^0-9]+/g, '');
      if (this.userAccount.phone) {
        const lastChar = this.userAccount.phone.substring(this.userAccount.phone.length - 1);
        if (!allowedChars.includes(lastChar)) {
          this.userAccount.phone = this.userAccount.phone.substring(0, this.userAccount.phone.length - 1);
        }
      }
    } else if (changed === 1) {
      if (this.userAccount.mobile) {
        const lastChar = this.userAccount.mobile.substr(this.userAccount.mobile.length - 1);
        if (!allowedChars.includes(lastChar)) {
          this.userAccount.mobile = this.userAccount.mobile.substring(0, this.userAccount.mobile.length - 1);
        }
      }
    }
  }

}

/** Interface to access user account details */
interface UserAccount {
  userId: string;
  generalMarketIds: string[];
  username: string;
  password: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  mobile: string;
  roleId: string;
  roleName: string;
  roleIds: string[];
  enabled: boolean;
  twoFactor: boolean;
  challenge: boolean;
  challengeQuestion: string;
  challengeAnswer: string;
}


